import { Component, TemplateRef, inject, viewChild } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { miGenericReports } from '@lenderhub/pages/mi/mi-generic-report-viewer/mi-generic-report-viewer.component';
import { HeaderComponent as LibHeaderComponent } from '@shared/components/header/header.component';
import { UserFeedbackComponent } from '@shared/components/user-feedback';
import { roles } from '@shared/constants';
import { ClientApps } from '@shared/models/enums';
import { LenderHubService, ModalService } from '@shared/services';
import { AuthorizeService } from '@shared/services/authorize.service';

@Component({
  selector: 'app-header',
  styleUrls: ['header.component.scss'],
  templateUrl: 'header.component.html',
  imports: [LibHeaderComponent, RouterLinkActive, RouterLink, UserFeedbackComponent],
})
export class HeaderComponent {
  public authService = inject(AuthorizeService);
  public lenderHubService = inject(LenderHubService);
  private readonly modalService = inject(ModalService);

  public readonly userFeedbackTemplate = viewChild.required<TemplateRef<unknown>>('userFeedbackTemplate');
  public roles = roles;
  public clientApp: ClientApps = ClientApps.LenderHub;

  public get isLoggedIn(): boolean {
    return this.authService.isLoggedIn;
  }

  public hasRole(role: string): boolean {
    return this.authService.hasRole(role);
  }

  public get hasPaidCompletionsMi() {
    return this.authService.hasRole(roles.lenderPaidMiRoles.paidCompletionsMi);
  }

  public get hasHostedMi() {
    return this.authService.hasRole(roles.lenderPaidMiRoles.hostedMi);
  }

  public get hasLGSSLenderSpecificMI() {
    return this.authService.hasRole(roles.lenderPaidMiRoles.lGSSLenderSpecificMI);
  }

  public get hasLGSSNewBuildMI() {
    return this.authService.hasRole(roles.lenderPaidMiRoles.lGSSNewBuildMI);
  }

  public get hasAccessToMi() {
    // Check for both paid MI and LGSS to show MI Insights tab as LGSS is not under LenderPaidMi role
    const paidMi = this.authService.hasRole(roles.lenderPaidMi);
    const lgss =
      this.authService.hasRole(roles.lenderPaidMiRoles.lGSSLenderSpecificMI) ||
      this.authService.hasRole(roles.lenderPaidMiRoles.lGSSNewBuildMI);
    return paidMi || lgss;
  }

  public get firstMiLink() {
    if (this.hasPaidCompletionsMi) {
      return '/mi/completions-paid';
    } else if (this.hasHostedMi) {
      return '/mi/hosted';
    } else if (this.hasLGSSLenderSpecificMI || this.hasLGSSNewBuildMI) {
      return '/mi/surveying-services';
    }

    const firstGenericReport = miGenericReports.find(x => this.authService.hasRole(x.role));
    if (firstGenericReport) {
      return `/mi/${firstGenericReport.path}`;
    }

    return null;
  }

  public get imgIsSvg() {
    return (this.lenderHubService.lenderName?.lenderLogoUrl ?? '').endsWith('.svg');
  }

  public get lenderHasProducts() {
    return this.lenderHubService.lenderName?.hasProducts ?? false;
  }

  public showFeedbackModal() {
    this.modalService.open({
      title: 'Provide Feedback',
      component: this.userFeedbackTemplate(),
    });
  }
}
